import { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { NavTabs } from '@components/NavTabs';
import { useTranslation, useSurveysOptions, useLocations } from '@hooks';
import { RoutePath } from '@constants/routes';
import { childRoutePath } from '@helpers/navigation';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { GroupByFilter } from '@constants/analytics';
import { INCLUDE_COVER_MANAGER } from '@constants/feedbacks';
import { subtractYears } from '@helpers/dates';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './Feedback/FiltersPanel';
import Conversion from './Conversion';
import Feedback from './Feedback';
import SurveyInsights from './SurveyInsights';
import PageContainer from '@components/Layout/PageContainer';

const Container = styled(PageContainer)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
    '@media print': {
        height: 'auto',
        overflow: 'visible',
    },
});

const StyledNavTabs = styled(NavTabs)({
    '@media print': {
        display: 'none',
    },
});

const AnalyticsSurveysRoute = () => {
    const { pathname } = useLocation();
    const [currentTab, setCurrentTab] = useState(pathname);
    const { t } = useTranslation();

    const [filter, setFilter] = useState({
        locations: [],
        surveys: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterVersion, setFilterVersion] = useState(0);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const initDone = useRef(false);
    const feedbackFetchedVersion = useRef(-1);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const account = useSelector(state => state.account?.account);
    const { getSurveysByLocationIds } = useSurveysOptions();
    const { mapLocationsToFormValue } = useLocations();

    const isCoverManagerConnected = isIntegrationConnected(account, AccIntegrationName.CoverManager);

    const locationsWithSurveys = useMemo(
        () => locations.filter(location => location?.surveys?.length > 0),
        [locations],
    );

    useEffect(() => {
        if (locationsWithSurveys.length && !initDone.current) {
            const availableSurveys = getSurveysByLocationIds(locationsWithSurveys.map(l => l.companyId));
            const surveys = isCoverManagerConnected ? [...availableSurveys, INCLUDE_COVER_MANAGER] : availableSurveys;
            setFilter(prev => ({ ...prev, surveys, locations: mapLocationsToFormValue(locationsWithSurveys) }));
            initDone.current = true;
            setFilterVersion(1);
        }
    }, [locationsWithSurveys, isCoverManagerConnected]);

    const onApplyFilters = () => {
        setFilterVersion(v => v + 1);
    };

    const availableTabs = [
        { label: t('Sidebar.analyticsConversion'), value: RoutePath.SurveysAnalyticsConversion },
        { label: t('Sidebar.analyticsNpsOverview'), value: RoutePath.SurveysAnalyticsResponses },
        { label: t('Sidebar.analyticsSurveyInsights'), value: RoutePath.SurveysAnalyticsSurveyInsights },
    ];

    const RouteMap = {
        [RoutePath.SurveysAnalyticsConversion]: 0,
        [RoutePath.SurveysAnalyticsResponses]: 1,
        [RoutePath.SurveysAnalyticsSurveyInsights]: 2,
    };

    return (
        <Container>
            <AnalyticsWrapper>
                <HeaderWrapper>
                    <FiltersPanel
                        setFiltersOpened={setFiltersOpened}
                        filtersNumber={filterNumber}
                        filterOpened={filterOpened}
                        locations={locationsWithSurveys}
                        filter={filter}
                        setFilter={setFilter}
                        type="feedbacks"
                        setFilterNumber={setFilterNumber}
                        onApplyFilters={onApplyFilters}
                    />
                    <PrintButton />
                </HeaderWrapper>
            </AnalyticsWrapper>
            <StyledNavTabs
                tabLinks={availableTabs}
                onTabClick={selectedTab => setCurrentTab(selectedTab)}
                currentTabIndex={RouteMap[currentTab]}
            />
            <Routes>
                <Route
                    path={childRoutePath(RoutePath.SurveysAnalyticsConversion, RoutePath.SurveysAnalytics)}
                    element={<Conversion filter={filter} filterVersion={filterVersion} />}
                />
                <Route
                    path={childRoutePath(RoutePath.SurveysAnalyticsResponses, RoutePath.SurveysAnalytics)}
                    element={
                        <Feedback
                            filter={filter}
                            filterVersion={filterVersion}
                            feedbackFetchedVersion={feedbackFetchedVersion}
                        />
                    }
                />
                <Route
                    path={childRoutePath(RoutePath.SurveysAnalyticsSurveyInsights, RoutePath.SurveysAnalytics)}
                    element={
                        <SurveyInsights
                            filter={filter}
                            filterVersion={filterVersion}
                            feedbackFetchedVersion={feedbackFetchedVersion}
                        />
                    }
                />
                <Route path="*" element={<Navigate to={RoutePath.SurveysAnalyticsConversion} replace />} />
            </Routes>
        </Container>
    );
};

export default AnalyticsSurveysRoute;
